// Apple In-App Purchase integration via expo-iap (v4.x).
//
// expo-iap 4.x uses a per-platform request shape:
//   requestPurchase({ request: { apple: {...}, google: {...} }, type: 'subs' })
// and `fetchProducts({ skus, type })` instead of legacy `getProducts(skus)`,
// and `finishTransaction({ purchase, isConsumable })` taking the full purchase
// object rather than a transactionId string.
//
// The mobile UI calls `startApplePurchase(productId)`. This module:
//   1. Tells the billing service to mint an `appAccountToken` (purchaseInitiate).
//   2. Calls expo-iap's requestPurchase with the token attached.
//   3. Awaits the StoreKit purchase update via the IAP listener.
//   4. Sends the resulting signedTransactionInfo (purchaseToken / JWS) to
//      billing for verification + sync.
//   5. Finishes the StoreKit transaction so Apple stops re-delivering it.
//
// All Apple JWS verification happens server-side. We never trust client-decoded
// transactions for entitlement decisions.

import { Platform } from 'react-native';
import { purchaseInitiate, purchaseFinalizeApple, restorePurchases } from './index';
import debug from '../../utilities/debug';

// expo-iap is dynamically required so the JS build doesn't fail before the
// native module is linked. Types are intentionally loose here — we only touch
// a small surface of the library.
type ExpoIAPModule = {
  initConnection: () => Promise<boolean>;
  endConnection: () => Promise<boolean>;
  fetchProducts: (args: { skus: string[]; type?: 'in-app' | 'subs' | 'all' }) => Promise<any[]>;
  requestPurchase: (args: any) => Promise<any>;
  finishTransaction: (args: { purchase: any; isConsumable?: boolean }) => Promise<void>;
  getAvailablePurchases: (args?: any) => Promise<any[]>;
  showManageSubscriptionsIOS?: () => Promise<any[]>;
  presentCodeRedemptionSheetIOS?: () => Promise<boolean>;
  deepLinkToSubscriptions?: (args?: any) => Promise<void>;
  purchaseUpdatedListener: (cb: (purchase: any) => void) => { remove: () => void };
  purchaseErrorListener: (cb: (err: any) => void) => { remove: () => void };
};

let _iap: ExpoIAPModule | null = null;
const loadIap = (): ExpoIAPModule | null => {
  if (_iap) return _iap;
  try {
    _iap = require('expo-iap') as ExpoIAPModule;
    return _iap;
  } catch (err: any) {
    debug.warn('[iap] expo-iap not installed yet — call after `npx expo install expo-iap`', { err: err?.message });
    return null;
  }
};

let _connected = false;
let _updateListener: { remove: () => void } | null = null;
let _errorListener: { remove: () => void } | null = null;

// Active purchase resolution map: productId -> { resolve, reject }. Resolved
// with the full `purchase` object so callers can both read the JWS token and
// finish the transaction by handing the same object back to expo-iap.
const _pending = new Map<string, { resolve: (purchase: any) => void; reject: (err: Error) => void }>();

// Tokens already handed to the unsolicited-purchase sync this session, so a
// transaction StoreKit emits more than once doesn't double-post to billing.
const _syncedTokens = new Set<string>();

// Registered by startIapObserver so a background sync (a stranded purchase
// healed on launch) can tell the app to refresh account/billing state.
let _onUnsolicitedPurchaseSynced: (() => void) | null = null;

const extractToken = (purchase: any): string | null => {
  // expo-iap v4 unifies the JWS string on `purchaseToken`. Fall back to the
  // legacy fields for safety in case a host app pinned an older build.
  return purchase?.purchaseToken
    || purchase?.transactionReceipt
    || purchase?.jwsRepresentation
    || null;
};

export const isAppleIAPAvailable = (): boolean => Platform.OS === 'ios' && !!loadIap();

export async function ensureIapConnection(): Promise<boolean> {
  if (Platform.OS !== 'ios') return false;
  const iap = loadIap();
  if (!iap) return false;
  if (_connected) return true;
  try {
    await iap.initConnection();
    _connected = true;
    _updateListener = iap.purchaseUpdatedListener((purchase) => {
      const productId = purchase?.productId || purchase?.id;
      const token = extractToken(purchase);
      if (productId && token && _pending.has(productId)) {
        const handlers = _pending.get(productId)!;
        _pending.delete(productId);
        handlers.resolve(purchase);
        return;
      }
      // Unsolicited transaction — no startApplePurchase call is waiting for
      // it. This is StoreKit re-delivering a purchase the app never finished:
      // a purchase that completed after the 60s confirmation timeout or after
      // the app was killed mid-flow, an offer-code redemption, or a finalize
      // whose backend call failed. Dropping these (the old behavior) left the
      // user charged by Apple with no plan on their account, and every later
      // purchase attempt dead-ended on "Item already owned". Sync it to the
      // backend instead, and only finish it once billing has recorded it.
      if (token) void syncUnsolicitedPurchase(purchase, token);
    });
    _errorListener = iap.purchaseErrorListener((err) => {
      debug.warn('[iap] purchase error event', { err });
      const sku = err?.productId;
      if (sku && _pending.has(sku)) {
        const handlers = _pending.get(sku)!;
        _pending.delete(sku);
        const error = new Error(err?.message || 'Purchase failed') as Error & { code?: string };
        // Keep the machine-readable code so catch sites can classify the
        // failure (isAlreadyOwnedError checks it alongside the message).
        error.code = err?.code ?? err?.errorCode;
        handlers.reject(error);
      }
    });
    return true;
  } catch (err: any) {
    debug.error('[iap] initConnection failed', { err: err?.message });
    return false;
  }
}

export async function teardownIap(): Promise<void> {
  if (Platform.OS !== 'ios') return;
  const iap = loadIap();
  if (!iap) return;
  try {
    _updateListener?.remove();
    _errorListener?.remove();
    _updateListener = null;
    _errorListener = null;
    if (_connected) {
      await iap.endConnection();
      _connected = false;
    }
  } catch (err: any) {
    debug.warn('[iap] teardown error', { err: err?.message });
  }
}

/**
 * Push a StoreKit transaction nobody is awaiting through the backend restore
 * endpoint, then finish it so Apple stops re-delivering it.
 *
 * Restore (not finalize) is deliberate: it works for receipts with no
 * appAccountToken (offer-code redemptions, re-subscribes made directly in the
 * App Store) by falling back to the calling account, and it re-confirms the
 * subscription with Apple's status API before applying.
 *
 * Outcomes:
 *   - applied            -> finishTransaction + notify the app to refresh
 *   - active, not applied-> leave UNFINISHED. The receipt belongs to a
 *                           different Eleplan account or the backend refused
 *                           it; re-delivery on a later launch (maybe under the
 *                           right login) is the retry mechanism.
 *   - expired / revoked  -> finishTransaction (dead sub — stop the re-delivery
 *                           churn), no entitlement change.
 */
async function syncUnsolicitedPurchase(purchase: any, token: string): Promise<void> {
  if (_syncedTokens.has(token)) return;
  _syncedTokens.add(token);
  try {
    const result = await restorePurchases([token]);
    const entry = (result.restored || [])[0];
    if (entry && entry.active === false) {
      const iap = loadIap();
      try { await iap?.finishTransaction({ purchase, isConsumable: false }); }
      catch (err: any) { debug.warn('[iap] finishTransaction for expired stranded purchase failed (non-fatal)', { err: err?.message }); }
      return;
    }
    const applied = !!entry && entry.active && entry.applied !== false;
    if (!applied) {
      _syncedTokens.delete(token);
      debug.warn('[iap] stranded purchase could not be attached to this account; leaving unfinished', {
        skipped: (result as any)?.skipped,
      });
      return;
    }
    const iap = loadIap();
    try { await iap?.finishTransaction({ purchase, isConsumable: false }); }
    catch (err: any) { debug.warn('[iap] finishTransaction after stranded-purchase sync failed (non-fatal)', { err: err?.message }); }
    debug.log('billing', '[iap] stranded purchase synced to billing', { originalTransactionId: entry.originalTransactionId });
    try { _onUnsolicitedPurchaseSynced?.(); } catch (_) { /* observer callback must never break the sync */ }
  } catch (err: any) {
    _syncedTokens.delete(token);
    debug.warn('[iap] stranded purchase sync failed (will retry on next delivery)', { err: err?.message });
  }
}

/**
 * App-launch hook (iOS only, call once signed in). Opens the StoreKit
 * connection so transactions StoreKit re-delivers on launch reach
 * purchaseUpdatedListener and get synced via syncUnsolicitedPurchase.
 *
 * Because the purchase flow only finishes a transaction AFTER the backend
 * records it, "stranded" is exactly "unfinished" — StoreKit re-delivers every
 * unfinished transaction on launch, so this heals accounts whose purchase
 * never reached billing without polling any endpoint. Without it, those
 * transactions are only picked up if the user happens to open a billing
 * surface (and before this module existed, not even then).
 */
export async function startIapObserver(onPurchaseSynced?: () => void): Promise<void> {
  if (Platform.OS !== 'ios') return;
  if (onPurchaseSynced) _onUnsolicitedPurchaseSynced = onPurchaseSynced;
  await ensureIapConnection();
}

export async function getAppleProducts(productIds: string[]): Promise<any[]> {
  const iap = loadIap();
  if (!iap) return [];
  await ensureIapConnection();
  try {
    return await iap.fetchProducts({ skus: productIds, type: 'subs' });
  } catch (err: any) {
    debug.error('[iap] fetchProducts failed', { err: err?.message });
    return [];
  }
}

// Apple/expo-iap "Item already owned" — the user has an active subscription for
// this product that our backend hasn't recorded (an earlier finalize failed, or
// they reinstalled / switched devices). Apple refuses to re-sell it. Detected
// across expo-iap's error code and the raw StoreKit message.
export function isAlreadyOwnedError(err: any): boolean {
  // Strip separators so E_ALREADY_OWNED / already-owned / ALREADY_OWNED all match.
  const code = String(err?.code ?? err?.errorCode ?? '').toUpperCase().replace(/[^A-Z]/g, '');
  const msg = String(err?.message ?? '').toLowerCase();
  return code.includes('ALREADYOWNED') || msg.includes('already owned');
}

// "Item already owned" isn't a purchase failure — Apple won't re-sell an
// active subscription. Recover by restoring: fetch the owned entitlement and
// finalize it on our backend (identical to a manual "Restore Purchases").
// This unsticks users whose earlier finalize failed, and handles reinstalls
// / device switches, instead of dead-ending on a scary "Purchase failed".
async function recoverFromAlreadyOwned(): Promise<{ success: boolean; message?: string }> {
  debug.log('billing', '[iap] requestPurchase reported already-owned; auto-restoring');
  const restored = await startAppleRestore();
  if (restored.success && restored.restored > 0) {
    return { success: true, message: 'Restored your existing subscription.' };
  }
  return {
    success: false,
    message: restored.message || 'You already own this subscription — try "Restore Purchases".',
  };
}

/**
 * End-to-end Apple purchase: mint token, request purchase, await receipt,
 * finalize on backend, finish StoreKit transaction.
 */
export async function startApplePurchase(productId: string): Promise<{
  success: boolean;
  message?: string;
  errorCode?: string;
}> {
  if (Platform.OS !== 'ios') return { success: false, message: 'iOS only.' };
  const iap = loadIap();
  if (!iap) return { success: false, message: 'In-app purchases unavailable.' };
  const ok = await ensureIapConnection();
  if (!ok) return { success: false, message: 'Could not connect to App Store.' };

  let appAccountToken: string;
  try {
    const initiate = await purchaseInitiate(productId);
    if (initiate.platform !== 'apple' || !initiate.appAccountToken) {
      return { success: false, message: 'Backend did not authorize an Apple purchase for this product.' };
    }
    appAccountToken = initiate.appAccountToken;
  } catch (err: any) {
    // Pass the server-side errorCode (e.g. EXISTING_STRIPE_SUBSCRIPTION)
    // through so the UI can show the right modal / deep-link instead of a
    // generic "Purchase failed" alert.
    return { success: false, message: err?.message || 'Could not start purchase.', errorCode: err?.errorCode };
  }

  // Set up the resolution promise BEFORE requesting purchase to avoid race.
  // The listener path covers brand-new purchases. The `requestPurchase` return
  // value covers crossgrades/replace-subscription cases where StoreKit doesn't
  // emit a new event because the underlying transaction is the same row.
  let timeoutHandle: ReturnType<typeof setTimeout> | null = null;
  const receivedFromListener = new Promise<any>((resolve, reject) => {
    _pending.set(productId, { resolve, reject });
    timeoutHandle = setTimeout(() => {
      if (_pending.has(productId)) {
        _pending.delete(productId);
        reject(new Error('Purchase timed out waiting for App Store confirmation.'));
      }
    }, 60_000);
  });

  let directResult: any = null;
  try {
    // expo-iap v4: per-platform request payload + explicit `type: 'subs'` for
    // auto-renewable subscriptions. The legacy flat shape ({ sku, ... }) blows
    // up internally with "Cannot read property 'apple' of undefined".
    directResult = await iap.requestPurchase({
      request: {
        apple: {
          sku: productId,
          appAccountToken,
        },
      },
      type: 'subs',
    });
  } catch (err: any) {
    _pending.delete(productId);
    if (timeoutHandle) clearTimeout(timeoutHandle);
    if (isAlreadyOwnedError(err)) {
      return recoverFromAlreadyOwned();
    }
    return { success: false, message: err?.message || 'Apple did not accept the purchase request.' };
  }

  // If StoreKit handed us the purchase synchronously (crossgrade, restore,
  // already-pending upgrade), use it directly and don't wait for an event
  // that won't come.
  const directPurchase = Array.isArray(directResult) ? directResult[0] : directResult;
  const directToken = extractToken(directPurchase);
  let purchase: any;
  let signedTransactionInfo: string;
  if (directPurchase && directToken) {
    purchase = directPurchase;
    signedTransactionInfo = directToken;
    _pending.delete(productId);
    if (timeoutHandle) clearTimeout(timeoutHandle);
  } else {
    try {
      purchase = await receivedFromListener;
      signedTransactionInfo = extractToken(purchase) || '';
      if (!signedTransactionInfo) {
        return { success: false, message: 'Apple returned an empty transaction.' };
      }
    } catch (err: any) {
      // Same recovery as the direct-rejection path above. Some expo-iap
      // versions/flows resolve requestPurchase with nothing and deliver the
      // failure through purchaseErrorListener instead — without this check,
      // an already-owned error on that path surfaced the raw dialog with no
      // auto-restore.
      if (isAlreadyOwnedError(err)) {
        return recoverFromAlreadyOwned();
      }
      return { success: false, message: err?.message || 'Purchase did not complete.' };
    } finally {
      // The listener settled the promise (either way) — disarm the 60s
      // watchdog so it doesn't linger for a purchase that already concluded.
      if (timeoutHandle) clearTimeout(timeoutHandle);
    }
  }

  try {
    const result = await purchaseFinalizeApple(signedTransactionInfo);
    if (!result.applied) {
      return { success: false, message: 'Backend rejected the receipt.' };
    }
    // StoreKit often delivers the same purchase over BOTH the requestPurchase
    // return value and the update listener. The finalize succeeded, so mark
    // the token synced — a straggler listener event for it must not re-post
    // through the unsolicited-purchase path. (On finalize failure we leave it
    // unmarked on purpose: re-delivery is the retry mechanism.)
    _syncedTokens.add(signedTransactionInfo);
  } catch (err: any) {
    return { success: false, message: err?.message || 'Could not record purchase.' };
  }

  // Tell StoreKit we processed the transaction (very important — otherwise
  // Apple keeps re-delivering it on every launch). v4 API takes the full
  // purchase object rather than a transactionId string.
  try {
    await iap.finishTransaction({ purchase, isConsumable: false });
  } catch (err: any) {
    debug.warn('[iap] finishTransaction failed (non-fatal)', { err: err?.message });
  }

  return { success: true };
}

/**
 * Present Apple's native Manage Subscriptions sheet (StoreKit 2's
 * `AppStore.showManageSubscriptions(in:)`) without ever leaving the app.
 * Available iOS 15+. When the user closes the sheet, expo-iap returns the
 * subscriptions that changed (cancelled, etc.) so the caller can refresh
 * billing state immediately instead of waiting for the App Store Server
 * Notification to round-trip.
 *
 * Returns:
 *   - { success: true, changed: PurchaseIOS[] }                      // sheet ran
 *   - { success: false, message }                                    // failed pre-iOS-15 / sheet error
 */
export async function showManageAppleSubscriptions(): Promise<{ success: boolean; changed?: any[]; message?: string }> {
  if (Platform.OS !== 'ios') return { success: false, message: 'iOS only.' };
  const iap = loadIap();
  if (!iap?.showManageSubscriptionsIOS) {
    return { success: false, message: 'In-app subscription management unavailable.' };
  }
  await ensureIapConnection();
  try {
    const changed = await iap.showManageSubscriptionsIOS();
    return { success: true, changed: Array.isArray(changed) ? changed : [] };
  } catch (err: any) {
    debug.warn('[iap] showManageSubscriptionsIOS failed', { err: err?.message });
    return { success: false, message: err?.message || 'Could not open subscription manager.' };
  }
}

/**
 * Present Apple's native code-redemption sheet for offer / promo codes.
 *
 * What the user redeems here is an Apple-issued promo code (App Store
 * Connect → Users and Access → Sandbox Testers → Promo Codes for paid
 * apps, or App Store Connect → Subscriptions → Offer Codes for sub
 * discounts). This is NOT a third-party / Stripe-style coupon — Apple
 * doesn't accept those for IAP. The sheet itself is a system overlay
 * driven by StoreKit; we hand control to Apple and react to whatever
 * comes back through the standard transaction listener.
 *
 * Returns success: true the moment the sheet was presented (whether
 * the user actually redeemed something is not surfaced — Apple delivers
 * any resulting transaction through the normal `purchaseUpdatedListener`
 * → `applePlatformEvent` path we already use for purchases). Caller
 * should follow up with billing.refresh() / refreshAccount() so the
 * UI reflects the redeemed offer once the transaction lands.
 *
 * iOS-only. Real-device only — the sheet is a no-op in the simulator,
 * which expo-iap surfaces as a thrown error; we treat that as a soft
 * failure rather than crashing the UI.
 */
export async function presentAppleCodeRedemption(): Promise<{ success: boolean; message?: string }> {
  if (Platform.OS !== 'ios') return { success: false, message: 'iOS only.' };
  const iap = loadIap();
  if (!iap?.presentCodeRedemptionSheetIOS) {
    return { success: false, message: 'Code redemption unavailable on this device.' };
  }
  await ensureIapConnection();
  try {
    const presented = await iap.presentCodeRedemptionSheetIOS();
    if (!presented) {
      return { success: false, message: 'Apple could not present the redemption sheet.' };
    }
    return { success: true };
  } catch (err: any) {
    debug.warn('[iap] presentCodeRedemptionSheetIOS failed', { err: err?.message });
    return { success: false, message: err?.message || 'Could not open code redemption.' };
  }
}

/**
 * Pulls the current set of Apple-available purchases for this Apple ID and
 * sends them to the backend for verification + sync. User-initiated.
 */
export async function startAppleRestore(): Promise<{ success: boolean; restored: number; message?: string }> {
  if (Platform.OS !== 'ios') return { success: false, restored: 0, message: 'iOS only.' };
  const iap = loadIap();
  if (!iap) return { success: false, restored: 0, message: 'In-app purchases unavailable.' };
  const ok = await ensureIapConnection();
  if (!ok) return { success: false, restored: 0, message: 'Could not connect to App Store.' };

  let purchases: any[];
  try {
    purchases = await iap.getAvailablePurchases();
  } catch (err: any) {
    return { success: false, restored: 0, message: err?.message || 'Could not read receipts.' };
  }

  const signedTransactions: string[] = purchases
    .map((p) => extractToken(p))
    .filter((x): x is string => typeof x === 'string' && x.length > 0);

  if (!signedTransactions.length) {
    return { success: true, restored: 0 };
  }

  try {
    const result = await restorePurchases(signedTransactions);
    const entries = result.restored || [];
    // `active` alone isn't success: the backend can confirm a sub is live
    // with Apple yet still refuse to attach it (applied: false) or skip the
    // receipt entirely (bound to a different Eleplan account). Counting those
    // as restored told the user "Restored!" while their account stayed free.
    const appliedCount = entries.filter((r) => r.active && r.applied !== false).length;
    if (appliedCount > 0) {
      return { success: true, restored: appliedCount };
    }
    const skippedOtherAccount = (result.skipped || []).some((s) => s.reason === 'other_account');
    const activeUnapplied = entries.some((r) => r.active && r.applied === false);
    let message: string | undefined;
    if (skippedOtherAccount) {
      message = 'Your App Store subscription is linked to a different Eleplan account. Sign in with the account you originally subscribed with, or contact support to move it.';
    } else if (activeUnapplied) {
      message = 'We found your App Store subscription but could not attach it to this account. Please contact support.';
    }
    return { success: true, restored: 0, message };
  } catch (err: any) {
    return { success: false, restored: 0, message: err?.message || 'Restore failed.' };
  }
}
